export interface IEasingFunction {
	(k: number): number;
}

export const Easing = {
	Linear: {
		None: (k: number) => k,
	},

	Quadratic: {
		In: (k: number) => k * k,

		Out: (k: number) => k * (2 - k),

		InOut: (k: number) => {
			if ((k *= 2) < 1) {
				return 0.5 * k * k;
			}

			return -0.5 * (--k * (k - 2) - 1);
		},
	},

	Cubic: {
		In: (k: number) => k * k * k,

		Out: (k: number) => --k * k * k + 1,

		InOut: (k: number) => {
			if ((k *= 2) < 1) {
				return 0.5 * k * k * k;
			}

			return 0.5 * ((k -= 2) * k * k + 2);
		},
	},

	InverseCubic: {
		In: (k: number) => --k * k * k + 1,

		Out: (k: number) => k * k * k,

		InOut: (k: number) => {
			if ((k *= 2) < 1) {
				return 0.5 * ((k -= 2) * k * k + 2);
			}

			return 0.5 * k * k * k;
		},
	},

	Quartic: {
		In: (k: number) => k * k * k * k,

		Out: (k: number) => 1 - --k * k * k * k,

		InOut: (k: number) => {
			if ((k *= 2) < 1) {
				return 0.5 * k * k * k * k;
			}

			return -0.5 * ((k -= 2) * k * k * k - 2);
		},
	},

	Quintic: {
		In: (k: number) => k * k * k * k * k,

		Out: (k: number) => --k * k * k * k * k + 1,

		InOut: (k: number) => {
			if ((k *= 2) < 1) {
				return 0.5 * k * k * k * k * k;
			}

			return 0.5 * ((k -= 2) * k * k * k * k + 2);
		},
	},

	Sinusoidal: {
		In: (k: number) => 1 - Math.cos((k * Math.PI) / 2),

		Out: (k: number) => Math.sin((k * Math.PI) / 2),

		InOut: (k: number) => 0.5 * (1 - Math.cos(Math.PI * k)),
	},

	Exponential: {
		In: (k: number) => (k === 0 ? 0 : Math.pow(1024, k - 1)),

		Out: (k: number) => (k === 1 ? 1 : 1 - Math.pow(2, -10 * k)),

		InOut: (k: number) => {
			if (k === 0) {
				return 0;
			}

			if (k === 1) {
				return 1;
			}

			if ((k *= 2) < 1) {
				return 0.5 * Math.pow(1024, k - 1);
			}

			return 0.5 * (-Math.pow(2, -10 * (k - 1)) + 2);
		},
	},

	Circular: {
		In: (k: number) => 1 - Math.sqrt(1 - k * k),

		Out: (k: number) => Math.sqrt(1 - --k * k),

		InOut: (k: number) => {
			if ((k *= 2) < 1) {
				return -0.5 * (Math.sqrt(1 - k * k) - 1);
			}

			return 0.5 * (Math.sqrt(1 - (k -= 2) * k) + 1);
		},
	},

	Elastic: {
		In: (k: number) => {
			if (k === 0) {
				return 0;
			}

			if (k === 1) {
				return 1;
			}

			return -Math.pow(2, 10 * (k - 1)) * Math.sin((k - 1.1) * 5 * Math.PI);
		},

		Out: (k: number) => {
			if (k === 0) {
				return 0;
			}

			if (k === 1) {
				return 1;
			}

			return Math.pow(2, -10 * k) * Math.sin((k - 0.1) * 5 * Math.PI) + 1;
		},

		InOut: (k: number) => {
			if (k === 0) {
				return 0;
			}

			if (k === 1) {
				return 1;
			}

			k *= 2;

			if (k < 1) {
				return -0.5 * Math.pow(2, 10 * (k - 1)) * Math.sin((k - 1.1) * 5 * Math.PI);
			}

			return 0.5 * Math.pow(2, -10 * (k - 1)) * Math.sin((k - 1.1) * 5 * Math.PI) + 1;
		},
	},

	Back: {
		In: (k: number) => {
			const s = 1.70158;

			return k * k * ((s + 1) * k - s);
		},

		Out: (k: number) => {
			const s = 1.70158;

			return --k * k * ((s + 1) * k + s) + 1;
		},

		InOut: (k: number) => {
			const s = 1.70158 * 1.525;

			if ((k *= 2) < 1) {
				return 0.5 * (k * k * ((s + 1) * k - s));
			}

			return 0.5 * ((k -= 2) * k * ((s + 1) * k + s) + 2);
		},
	},

	Bounce: {
		In: (k: number) => 1 - Easing.Bounce.Out(1 - k),

		Out: (k: number) => {
			if (k < 1 / 2.75) {
				return 7.5625 * k * k;
			} else if (k < 2 / 2.75) {
				return 7.5625 * (k -= 1.5 / 2.75) * k + 0.75;
			} else if (k < 2.5 / 2.75) {
				return 7.5625 * (k -= 2.25 / 2.75) * k + 0.9375;
			} else {
				return 7.5625 * (k -= 2.625 / 2.75) * k + 0.984375;
			}
		},

		InOut: (k: number) => {
			if (k < 0.5) {
				return Easing.Bounce.In(k * 2) * 0.5;
			}

			return Easing.Bounce.Out(k * 2 - 1) * 0.5 + 0.5;
		},
	},

	SingleBounce: {
		In: (k: number) => 1 - Easing.Bounce.Out(1 - k),

		Out: (k: number) => {
			if (k < 1.75 / 2.75) {
				return 2.469387 * k * k;
			} else {
				return 2.469387 * (k -= 2.25 / 2.75) * k + 0.91836738;
			}
		},

		InOut: (k: number) => {
			if (k < 0.5) {
				return Easing.Bounce.In(k * 2) * 0.5;
			}

			return Easing.Bounce.Out(k * 2 - 1) * 0.5 + 0.5;
		},
	},
};

export class Bezier {
	/**
	 * https://github.com/gre/bezier-easing
	 * BezierEasing - use bezier curve for transition easing function
	 * by Gaëtan Renaudeau 2014 - 2015 – MIT License
	 */
	protected static NEWTON_ITERATIONS: number = 4;
	protected static NEWTON_MIN_SLOPE: number = 0.001;
	protected static SUBDIVISION_PRECISION: number = 0.0000001;
	protected static SUBDIVISION_MAX_ITERATIONS: number = 10;

	protected static kSplineTableSize: number = 11;
	protected static kSampleStepSize: number = 1.0 / (Bezier.kSplineTableSize - 1.0);

	protected static float32ArraySupported: boolean = typeof Float32Array === 'function';

	protected static A(aA1: number, aA2: number): number {
		return 1.0 - 3.0 * aA2 + 3.0 * aA1;
	}

	protected static B(aA1: number, aA2: number): number {
		return 3.0 * aA2 - 6.0 * aA1;
	}

	protected static C(aA1: number): number {
		return 3.0 * aA1;
	}

	// Returns x(t) given t, x1, and x2, or y(t) given t, y1, and y2.
	protected static calcBezier(aT: number, aA1: any, aA2: any): number {
		return ((Bezier.A(aA1, aA2) * aT + Bezier.B(aA1, aA2)) * aT + Bezier.C(aA1)) * aT;
	}

	// Returns dx/dt given t, x1, and x2, or dy/dt given t, y1, and y2.
	protected static getSlope(aT: number, aA1: any, aA2: any): number {
		return 3.0 * Bezier.A(aA1, aA2) * aT * aT + 2.0 * Bezier.B(aA1, aA2) * aT + Bezier.C(aA1);
	}

	protected static binarySubdivide(aX: number, aA: number, aB: number, mX1: any, mX2: any): number {
		let currentX: number = 0;
		let currentT: any = 0;
		let i = 0;

		do {
			currentT = aA + (aB - aA) / 2.0;
			currentX = Bezier.calcBezier(currentT, mX1, mX2) - aX;
			if (currentX > 0.0) {
				aB = currentT;
			} else {
				aA = currentT;
			}
		} while (Math.abs(currentX) > Bezier.SUBDIVISION_PRECISION && ++i < Bezier.SUBDIVISION_MAX_ITERATIONS);

		return currentT;
	}

	protected static newtonRaphsonIterate(aX: number, aGuessT: number, mX1: any, mX2: any): number {
		for (let i = 0; i < Bezier.NEWTON_ITERATIONS; ++i) {
			const currentSlope = Bezier.getSlope(aGuessT, mX1, mX2);
			if (currentSlope === 0.0) {
				return aGuessT;
			}
			const currentX = Bezier.calcBezier(aGuessT, mX1, mX2) - aX;
			aGuessT -= currentX / currentSlope;
		}

		return aGuessT;
	}

	protected static LinearEasing(x: any): number {
		return x;
	}

	public static bezier(mX1: number, mY1: any, mX2: number, mY2: any): IEasingFunction {
		if (!(0 <= mX1 && mX1 <= 1 && 0 <= mX2 && mX2 <= 1)) {
			throw new Error('bezier x values must be in [0, 1] range');
		}

		if (mX1 === mY1 && mX2 === mY2) {
			return Bezier.LinearEasing;
		}

		// Precompute samples table
		const sampleValues = Bezier.float32ArraySupported ? new Float32Array(Bezier.kSplineTableSize) : new Array(Bezier.kSplineTableSize);

		for (let i = 0; i < Bezier.kSplineTableSize; ++i) {
			sampleValues[i] = Bezier.calcBezier(i * Bezier.kSampleStepSize, mX1, mX2);
		}

		function getTForX(aX: number): number {
			let intervalStart = 0.0;
			let currentSample = 1;
			const lastSample = Bezier.kSplineTableSize - 1;

			for (; currentSample !== lastSample && sampleValues[currentSample] <= aX; ++currentSample) {
				intervalStart += Bezier.kSampleStepSize;
			}
			--currentSample;

			// Interpolate to provide an initial guess for t
			const dist = (aX - sampleValues[currentSample]) / (sampleValues[currentSample + 1] - sampleValues[currentSample]);
			const guessForT = intervalStart + dist * Bezier.kSampleStepSize;

			const initialSlope = Bezier.getSlope(guessForT, mX1, mX2);
			if (initialSlope >= Bezier.NEWTON_MIN_SLOPE) {
				return Bezier.newtonRaphsonIterate(aX, guessForT, mX1, mX2);
			} else if (initialSlope === 0.0) {
				return guessForT;
			} else {
				return Bezier.binarySubdivide(aX, intervalStart, intervalStart + Bezier.kSampleStepSize, mX1, mX2);
			}
		}

		return function BezierEasing(x: number): number {
			// Because JavaScript number are imprecise, we should guarantee the extremes are right.
			if (x === 0 || x === 1) {
				return x;
			}

			return Bezier.calcBezier(getTForX(x), mY1, mY2);
		};
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/tween/Easing.ts